import React, { useState, useEffect } from 'react';
import styles from '../../Components/EditDiv/EditDiv.module.css'; // Use the same styles as AWForm
import buttonStyles from '../../Components/YesNoButtonsCSS/YesNoButtons.module.css';
import BlueLogo from "../../assets/BlueLogo.png"; // Import the Blue logo
import Dropdown from '../DropDownSelect/Dropdown';
import DatePicker from '../DatePicker/DatePicker'; // Import the DatePicker component
import moment from 'moment'; // Import moment for date formatting
import { useSelector, useDispatch } from "../../redux/store";
import { getJobs, getLevels } from "../../redux/slices/settings";

const teamOptions = [
  { id: 4, name: 'NZ' }
];

// Permission levels
// 1: User, 2: Admin, 3: Holiday organiser + Admin, 4: Guest
const permissionLevelOptions = [
  { id: 1, name: 'User' },
  { id: 2, name: 'Admin' },
  { id: 3, name: 'Holiday Organiser + Admin' },
  { id: 4, name: 'Guest' }
];

// Guest allowed pages options (keys as expected by backend / client mapping)
const guestPageOptions = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'opportunities', label: 'Opportunities' },
  { key: 'projects', label: 'Projects' },
  { key: 'timesheets', label: 'Time Sheets' },
  { key: 'clients', label: 'Clients' },
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'sharepoint', label: 'SharePoint' },
  { key: 'planner', label: 'Planner Board' },
  { key: 'resourcing', label: 'Resourcing' },
];

function OnboardEmployeePopup({ onSubmit, onClose }) {
  const dispatch = useDispatch();

  // Redux selectors for jobs and levels data
  const jobs = useSelector((state) => state.settings.jobs);
  const levels = useSelector((state) => state.settings.levels);
  const isJobsLoading = useSelector((state) => state.settings.isJobsLoading);
  const jobsError = useSelector((state) => state.settings.jobsError);

  // Fetch jobs and levels on component mount
  useEffect(() => {
    if (jobs.length === 0 && !isJobsLoading) {
      dispatch(getJobs());
    }
    if (levels.length === 0) {
      dispatch(getLevels());
    }
  }, [dispatch, jobs.length, levels.length, isJobsLoading]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // States for dropdowns - will store the ID
  const [teamId, setTeamId] = useState(teamOptions[0].id);
  const [jobId, setJobId] = useState(jobs.length > 0 ? jobs[0].id : null);
  const [permissionLevel, setPermissionLevel] = useState(permissionLevelOptions[0].id);
  const [guestAllowedPages, setGuestAllowedPages] = useState([]);

  // Original numeric/boolean fields
  const [sicknessAllowance, setSicknessAllowance] = useState(10);
  const [sicknessTaken, setSicknessTaken] = useState(0);
  const [holidayAdjustment, setHolidayAdjustment] = useState(0);
  const [holidayCarryOver, setHolidayCarryOver] = useState(0);
  const [totalHolidayAllowance, setTotalHolidayAllowance] = useState(25);
  const [holidayAllowance, setHolidayAllowance] = useState(25);
  const [totalStudyAllowance, setTotalStudyAllowance] = useState(5);
  const [studyAllowance, setStudyAllowance] = useState(5);
  const [startDate, setStartDate] = useState(moment().format('DD/MM/YYYY'));
  const [initials, setInitials] = useState('');
  const [weeklyWorkingHours, setWeeklyWorkingHours] = useState(40);
  const [timesheetsRequired, setTimesheetsRequired] = useState(true);
  const [isaHandler, setIsaHandler] = useState(false);
  const [feePropHandler, setFeePropHandler] = useState(false);
  const [fbpHandler, setFbpHandler] = useState(false);
  const [isaToSendHandler, setIsaToSendHandler] = useState(false);
  
  const [error, setError] = useState('');

  const [selectedJobTitle, setSelectedJobTitle] = useState(jobs.length > 0 ? jobs[0].title : '');
  const [selectedPermissionLevel, setSelectedPermissionLevel] = useState(permissionLevelOptions[0].name);
  const [selectedTeam, setSelectedTeam] = useState(teamOptions[0].name);

  // Create a map of level IDs to level names for displaying job titles with levels
  const levelMap = levels.reduce((map, level) => {
    map[level.id] = level.level;
    return map;
  }, {});

  // Format job display name with level information
  const formatJobTitle = (job) => {
    const levelName = levelMap[job.level_id] || job.level_code || '';
    return levelName ? `${job.title} (L${levelName})` : job.title;
  };

  // Parse level number from level string
  const parseLevelNumber = (raw) => {
    const val = (raw || '').toString().trim();
    if (!val) return NaN;
    const m = val.match(/^[Ll]\s*\.?\s*(\d+(?:\.\d+)?)/) || val.match(/^level\s*(\d+(?:\.\d+)?)/i) || val.match(/^(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : NaN;
  };

  // Sort jobs by level number (1-100 etc), then by title
  const sortedJobs = [...jobs].sort((a, b) => {
    const levelA = levelMap[a.level_id] || a.level_code || '';
    const levelB = levelMap[b.level_id] || b.level_code || '';
    const numA = parseLevelNumber(levelA);
    const numB = parseLevelNumber(levelB);

    // If both have valid level numbers, sort by level number
    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) return numA - numB;
    }
    // If one has a valid level number and the other doesn't, prioritize the one with a number
    else if (!isNaN(numA) && isNaN(numB)) return -1;
    else if (isNaN(numA) && !isNaN(numB)) return 1;

    // If levels are equal or both invalid, sort alphabetically by title
    return (a.title || '').localeCompare(b.title || '');
  });

  // Update selected job title when jobs data loads
  useEffect(() => {
    if (sortedJobs.length > 0 && !selectedJobTitle) {
      setSelectedJobTitle(formatJobTitle(sortedJobs[0]));
      setJobId(sortedJobs[0].id);
    }
  }, [sortedJobs, selectedJobTitle]);

  // Helper to derive initials from first and last name
  useEffect(() => {
    if (firstName && lastName) {
      setInitials((firstName[0] + lastName[0]).toUpperCase());
    } else if (firstName) {
      setInitials(firstName[0].toUpperCase());
    }
    else {
      setInitials('');
    }
  }, [firstName, lastName]);

  // Handle dropdown changes
  const handleJobTitleChange = (value) => {
    setSelectedJobTitle(value);
    // Find the job by matching the formatted display name
    const selectedJob = jobs.find(job => formatJobTitle(job) === value);
    setJobId(selectedJob ? selectedJob.id : null);
  };

  const handlePermissionLevelChange = (value) => {
    setSelectedPermissionLevel(value);
    const selectedPermission = permissionLevelOptions.find(perm => perm.name === value);
    setPermissionLevel(selectedPermission ? selectedPermission.id : 1);
  };

  const toggleGuestPage = (key) => {
    setGuestAllowedPages(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      return [...prev, key];
    });
  };

  const handleTeamChange = (value) => {
    setSelectedTeam(value);
    const selectedTeamObj = teamOptions.find(team => team.name === value);
    setTeamId(selectedTeamObj ? selectedTeamObj.id : 4);
  };

  // Handle date changes from DatePicker
  const handleDateChange = (newDate) => {
    setStartDate(moment(newDate.startDate).format('DD/MM/YYYY'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !initials.trim()) {
      setError('First Name, Last Name, Email, and Initials are required.');
      return;
    }
    if (!/\S+@(blueengineering\.co\.uk|re-structured\.com)$/.test(email)) {
      setError('Please enter a valid @blueengineering.co.uk or @re-structured.com email address.');
      return;
    }
    if (initials.length > 3) {
        setError('Initials can be max 3 characters.');
        return;
    }
    setError('');

    const formattedStartDate = moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');

    const formData = {
      first_name: firstName,
      last_name: lastName,
      work_email: email, 
      team_id: teamId,
      job_id: jobId,
      moment_admin_permission_level: permissionLevel,
      guest_allowed_pages: guestAllowedPages,
      sickness_allowance: parseInt(sicknessAllowance, 10),
      sickness_taken: parseInt(sicknessTaken, 10),
      holiday_adjustment: parseInt(holidayAdjustment, 10),
      holiday_carry_over: parseInt(holidayCarryOver, 10),
      total_holiday_allowance: parseInt(totalHolidayAllowance, 10),
      holiday_allowance: parseInt(holidayAllowance, 10),
      total_study_allowance: parseInt(totalStudyAllowance, 10),
      study_allowance: parseInt(studyAllowance, 10),
      start_date: formattedStartDate,
      initials: initials,
      weekly_working_hours: parseInt(weeklyWorkingHours, 10),
      timesheets_required: timesheetsRequired ? 1 : 0,
      ISA_handler: isaHandler ? 1 : 0,
      fee_prop_handler: feePropHandler ? 1 : 0,
      fbp_handler: fbpHandler ? 1 : 0,
      isa_to_send_handler: isaToSendHandler ? 1 : 0,
    };
    onSubmit(formData);
  };

  // --- Helper functions for Dropdown label/value management ---
  const getLabelById = (optionsArray, id, key = 'id', labelKey = 'name') => {
    const option = optionsArray.find(opt => opt[key] === id);
    return option ? option[labelKey] : '';
  };

  const handleDropdownChange = (setSelectedIdFunction, optionsArray, selectedLabel, key = 'id', labelKey = 'name') => {
    const selectedOption = optionsArray.find(opt => opt[labelKey] === selectedLabel);
    if (selectedOption) {
      setSelectedIdFunction(selectedOption[key]);
    }
  };

  const renderFormRow = (label, content) => {
    return (
      <div className={styles.List}>
        <p className={styles.dataTitleV2}>{label}: </p>
        <div className={styles.commentHolder}>
          {content}
        </div>
      </div>
    );
  };

  const renderInputField = (id, value, onChange, placeholder = "", type = "text") => {
    return (
      <input 
        id={id}
        className={styles.Comment}
        style={{ height: '20px' }}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    );
  };

  const renderCheckbox = (id, label, checked, onChange) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <input 
          type="checkbox" 
          id={id}
          checked={checked}
          onChange={onChange}
          style={{ marginRight: '8px' }}
        />
        <label htmlFor={id} style={{ fontFamily: 'PT Sans', fontSize: '13px' }}>
          {label}
        </label>
      </div>
    );
  };

  return (
    <div className={styles.backGround} style={{ position: "fixed", alignItems: "flex-start" }}>
      <div className={styles.form} style={{ width: "500px" }}>
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>Onboard New Employee</div>
        
        {error && (
          <div style={{ color: 'red', marginBottom: '15px', width: '100%', textAlign: 'center' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Basic Information */}
          <div style={{ marginTop: '15px', marginBottom: '10px', width: '100%' }}>
            <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>
              Basic Information
            </p>
          </div>

          {renderFormRow("First Name*", 
            renderInputField("firstName", firstName, (e) => setFirstName(e.target.value))
          )}

          {renderFormRow("Last Name*", 
            renderInputField("lastName", lastName, (e) => setLastName(e.target.value))
          )}

          {renderFormRow("Work Email*", 
            renderInputField("email", email, (e) => setEmail(e.target.value), "employee@blueengineering.co.uk or name@re-structured.com", "email")
          )}

          {renderFormRow("Initials*", 
            renderInputField("initials", initials, (e) => setInitials(e.target.value.toUpperCase()))
          )}

          {renderFormRow("Start Date*", 
            <div style={{ width: '100%' }}>
              <DatePicker
                initialDate={startDate}
                onDatesChange={handleDateChange}
                visableIcon={true}
              />
            </div>
          )}

          {/* Role & Permissions */}
          <div style={{ marginTop: '15px', marginBottom: '10px', width: '100%' }}>
            <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>
              Role & Permissions
            </p>
          </div>

          {renderFormRow("Team*", 
            <Dropdown
              options={teamOptions.map(opt => opt.name)}
              selectedValue={selectedTeam}
              onValueChange={handleTeamChange}
            />
          )}

          {renderFormRow("Job Title*",
            isJobsLoading ? (
              <div style={{ padding: '8px', color: '#666', fontStyle: 'italic' }}>
                Loading job titles...
              </div>
            ) : jobsError ? (
              <div style={{ padding: '8px', color: '#d32f2f', fontSize: '14px' }}>
                Error loading job titles: {jobsError}
              </div>
            ) : (
              <Dropdown
                options={sortedJobs.map(job => formatJobTitle(job))}
                selectedValue={selectedJobTitle}
                onValueChange={handleJobTitleChange}
              />
            )
          )}

          {renderFormRow("Permission Level*", 
            <Dropdown
              options={permissionLevelOptions.map(opt => opt.name)}
              selectedValue={selectedPermissionLevel}
              onValueChange={handlePermissionLevelChange}
            />
          )}

          {permissionLevel === 4 && (
            <>
              <div style={{ marginTop: '15px', marginBottom: '10px', width: '100%' }}>
                <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>
                  Guest Allowed Pages
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: '10px' }}>
                {guestPageOptions.map(p => (
                  <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'PT Sans', fontSize: '13px' }}>
                    <input
                      type="checkbox"
                      checked={guestAllowedPages.includes(p.key)}
                      onChange={() => toggleGuestPage(p.key)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </>
          )}

          {renderFormRow("Weekly Hours", 
            renderInputField("weeklyWorkingHours", weeklyWorkingHours, (e) => setWeeklyWorkingHours(e.target.value), "", "number")
          )}

          {/* Allowances */}
          <div style={{ marginTop: '15px', marginBottom: '10px', width: '100%' }}>
            <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>
              Allowances
            </p>
          </div>

          {renderFormRow("Sickness Allowance", 
            renderInputField("sicknessAllowance", sicknessAllowance, (e) => setSicknessAllowance(e.target.value), "", "number")
          )}

          {renderFormRow("Sickness Taken", 
            renderInputField("sicknessTaken", sicknessTaken, (e) => setSicknessTaken(e.target.value), "", "number")
          )}

          {renderFormRow("Contractual Holiday", 
            renderInputField("holidayAllowance", holidayAllowance, (e) => setHolidayAllowance(e.target.value), "", "number")
          )}

          {renderFormRow("Total Holiday", 
            renderInputField("totalHolidayAllowance", totalHolidayAllowance, (e) => setTotalHolidayAllowance(e.target.value), "", "number")
          )}

          {renderFormRow("Holiday Adjustment", 
            renderInputField("holidayAdjustment", holidayAdjustment, (e) => setHolidayAdjustment(e.target.value), "", "number")
          )}

          {renderFormRow("Holiday Carry Over", 
            renderInputField("holidayCarryOver", holidayCarryOver, (e) => setHolidayCarryOver(e.target.value), "", "number")
          )}

          {renderFormRow("Contractual Study", 
            renderInputField("studyAllowance", studyAllowance, (e) => setStudyAllowance(e.target.value), "", "number")
          )}

          {renderFormRow("Total Study", 
            renderInputField("totalStudyAllowance", totalStudyAllowance, (e) => setTotalStudyAllowance(e.target.value), "", "number")
          )}

          {/* System Access & Permissions */}
          <div style={{ marginTop: '15px', marginBottom: '10px', width: '100%' }}>
            <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#3C96D4', borderBottom: '1px solid #e0e0e0', paddingBottom: '5px' }}>
              System Access & Responsibilities
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <div style={{ width: '48%' }}>
              {renderCheckbox("timesheetsRequired", "Timesheets Required", timesheetsRequired, (e) => setTimesheetsRequired(e.target.checked))}
              {renderCheckbox("isaHandler", "ISA Handler", isaHandler, (e) => setIsaHandler(e.target.checked))}
              {renderCheckbox("feePropHandler", "Fee Proposal Handler", feePropHandler, (e) => setFeePropHandler(e.target.checked))}
            </div>
            <div style={{ width: '48%' }}>
              {renderCheckbox("fbpHandler", "FBP Handler", fbpHandler, (e) => setFbpHandler(e.target.checked))}
              {renderCheckbox("isaToSendHandler", "ISA To Send Handler", isaToSendHandler, (e) => setIsaToSendHandler(e.target.checked))}
            </div>
          </div>
        </form>

        <div className={styles.updateAndCancel}>
          <div className={styles.updateButton} onClick={handleSubmit}>
            <div className={buttonStyles.YesButtonComponentStyling}>SUBMIT</div>
          </div>
          <div className={styles.cancelButton} onClick={onClose}>
            <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
          </div>
        </div>

        <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" />
      </div>
    </div>
  );
}

export default OnboardEmployeePopup; 